// src/config/axiosInstance.ts
import axios, {
  AxiosInstance,
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import storage from "../../utils/storage/cryptoStorage";

// Logout helper fonksiyonu - circular import'u önlemek için
const performLogout = () => {
  storage.removeLocalStorage("token");
  storage.removeLocalStorage("refreshToken");
  storage.removeLocalStorage("user");

  // Redux persist verilerini temizle
  try {
    localStorage.removeItem('persist:bizmed-root');
  } catch (e) {
    console.warn('Tab verilerini temizlerken hata:', e);
  }
  // Login sayfasına yönlendir
  window.location.href = '/';
};

export const API_BASE_URL = process.env.REACT_APP_API_URL;

export const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
  headers: {
    Accept: "application/json",
  },
});

interface RefreshTokenResponse {
  data: {
    accesstoken: string;
    refreshtoken: string;
    refreshtokenexpiresat: string;
  };
  issuccess: boolean;
  statuscode: number;
  message: string;
}


let isRefreshing = false;
let failedQueue: {
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}[] = [];

const processQueue = (
  error: AxiosError | null,
  token: string | null = null
) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const decodeJWT = (token: string): { exp?: number } | null => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.warn("Token decode error:", e);
    return null;
  }
};

const isTokenExpired = (token: string): boolean => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return true;

  const expirationTime = decoded.exp * 1000;
  const currentTime = Date.now();
  const timeUntilExpiry = expirationTime - currentTime;
  const fiveMinutes = 5 * 60 * 1000;
  return timeUntilExpiry < fiveMinutes;
};

const refreshTokenIfNeeded = async (): Promise<string | null> => {
  const token = storage.getLocalStorage("token");
  if (!token) return null;

  if (!isTokenExpired(token)) {
    return token;
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then(() => {
        return storage.getLocalStorage("token");
      })
      .catch(() => {
        return null;
      });
  }

  const refreshToken = storage.getLocalStorage("refreshToken");
  if (!refreshToken) {
    console.warn("No refresh token available for proactive refresh");
    return token;
  }

  isRefreshing = true;

  try {
    const refreshResponse = await axios.post<RefreshTokenResponse>(
      `${API_BASE_URL}/auth/api/Auth/refresh-token`,
      { refreshToken: refreshToken }
    );

    const newAccessToken = refreshResponse.data.data.accesstoken;
    const newRefreshToken = refreshResponse.data.data.refreshtoken;

    storage.saveLocalStorage("token", newAccessToken);
    storage.saveLocalStorage("refreshToken", newRefreshToken);

    processQueue(null, newAccessToken);

    return newAccessToken;
  } catch (error) {
    console.error("Proactive token refresh failed:", error);
    processQueue(error as AxiosError);
    return token;
  } finally {
    isRefreshing = false;
  }
};

/** Proaktif yenileme; axios istek interceptor’u ile aynı (fetch/SSE için). */
export async function ensureAccessToken(): Promise<string | null> {
  return refreshTokenIfNeeded();
}

/**
 * 401 sonrası refresh; başarısızsa logout. `isRefreshing` ve `failedQueue` axios 401 akışı ile paylaşılır.
 */
export async function refreshSessionAndGetAccessToken(): Promise<string | null> {
  if (isRefreshing) {
    try {
      const token = await new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      });
      return token || storage.getLocalStorage("token");
    } catch {
      return null;
    }
  }

  const refreshToken = storage.getLocalStorage("refreshToken");
  if (!refreshToken) {
    console.error("No refresh token, redirecting to login.");
    performLogout();
    return null;
  }

  isRefreshing = true;
  try {
    const refreshResponse = await axios.post<RefreshTokenResponse>(
      `${API_BASE_URL}/auth/api/Auth/refresh-token`,
      { refreshToken }
    );

    const newAccessToken = refreshResponse.data.data.accesstoken;
    const newRefreshToken = refreshResponse.data.data.refreshtoken;

    storage.saveLocalStorage("token", newAccessToken);
    storage.saveLocalStorage("refreshToken", newRefreshToken);

    processQueue(null, newAccessToken);

    return newAccessToken;
  } catch (refreshError) {
    console.error("Failed to refresh token:", refreshError);
    performLogout();
    processQueue(refreshError as AxiosError);
    return null;
  } finally {
    isRefreshing = false;
  }
}

axiosInstance.interceptors.request.use(
  async (config) => {
    const token = await refreshTokenIfNeeded();
    const lang = localStorage.getItem("lang");

    config.headers["Accept-Language"] = lang || "tr";

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    console.error("Request error:", error.message);
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    const { status, data } = error.response || {};

    if (!originalRequest || typeof status === "undefined") {
      console.error(
        "Network or config error, no response status:",
        error.message
      );
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const newAccessToken = await refreshSessionAndGetAccessToken();
      if (!newAccessToken) {
        return Promise.reject(error);
      }

      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return axiosInstance(originalRequest);
    }

    switch (status) {
      case 400:
        console.error(`Bad Request (${status}):`, data);
        break;
      case 403:
        console.error(
          `Forbidden (${status}):`,
          "You don't have permission to access this resource.",
          data
        );
        break;
      case 404:
        console.error(
          `Not Found (${status}):`,
          "The requested resource was not found.",
          originalRequest?.url
        );
        break;
      case 500:
        console.error(
          `Server Error (${status}):`,
          "Something went wrong on the server.",
          data
        );
        break;
      default:
        console.error(`API Error (${status}):`, data);
        break;
    }

    if (!error.response && error.request) {
      console.error("Network Error: No response received.", error.message);
    } else if (!error.response && !error.request) {
      console.error("Axios config error:", error.message);
    }

    return Promise.reject(error);
  }
);
